// src/features/patient/care-treatment/components/CallsTab.tsx

/**
 * Purpose : The Calls, messages & letters tab: records that aren't in-person
 * visits, and doctors' notes with no visit (under Chart notes & documents), each with its real type name, date, author · department and the
 * first lines of its text as written, newest first under month headings.
 * The whole row (or card) opens the record; no links inside it. Underline
 * filter tabs with counts, the year picker and the List / Cards choice, as
 * on Visits.
 *
 * Contents :
 *
 * * const(CALL_FILTERS) / function(callFilterOf) : which chip a record belongs to
 * * function(groupCalls) : month headings, newest first
 * * function(CallsTab) : filters, year, view, month groups, rows or cards
 * * function(Preview) : the text in a fixed-height box, with Read more when it doesn't fit
 * * function(CallRow) / function(CallCard) : one record, opening it (all the same height)
 */

import { useLayoutEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { Select } from "@/shared/components/select";
import { cn } from "@/shared/lib/utils";
import { UnderlineTabs } from "@/shared/components/UnderlineTabs";
import type { Encounter, Note } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { formatDateOnly, nonVisits, noteTypeLabel, notesForVisit } from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel, visitKind } from "@/features/patient/care-treatment/utils/careNaming";
import { recordText } from "@/features/patient/care-treatment/utils/noteText";
import { KindTile } from "./careParts";
import {
  activeFilterClass,
  clickableCardClass,
  filterButtonClass,
  inactiveFilterClass,
  mutedIconClass,
  subTextClass,
  titleClass,
} from "./careStyles";
import { LoadOlderButton, usePaged } from "./LoadOlder";
import { readView, saveView, type VisitsView } from "./VisitsTimeline";

// const(CALL_FILTERS)
export type CallFilter = "all" | "calls" | "messages" | "results" | "other";

export const CALL_FILTERS: { id: CallFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "calls", label: "Phone calls" },
  { id: "messages", label: "Messages" },
  { id: "results", label: "Results reviewed" },
  { id: "other", label: "Chart notes & documents" },
];

// function(callFilterOf)
export function callFilterOf(record: Encounter): Exclude<CallFilter, "all"> {
  const kind = visitKind(record);
  if (kind === "phone_call") return "calls";
  if (kind === "message") return "messages";
  if (kind === "results") return "results";
  return "other";
}

const VIEW_KEY = "msq.careTreatment.callsView";

// function(groupCalls)
/** Newest first, under month headings ("" for an undated record). */
export function groupCalls(records: Encounter[]): { heading: string; records: Encounter[] }[] {
  const groups: { heading: string; records: Encounter[] }[] = [];
  for (const record of [...records].sort((a, b) => (b.start || "").localeCompare(a.start || ""))) {
    const [y, m] = [+record.start.slice(0, 4), +record.start.slice(5, 7)];
    const heading = Number.isNaN(y) || Number.isNaN(m) ? "" : new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
    const last = groups[groups.length - 1];
    if (last?.heading === heading) last.records.push(record); else groups.push({ heading, records: [record] });
  }
  return groups;
}

// function(CallsTab)
/**
 * @param encounters - Everything; only non-visit records are listed.
 * @param notes - Notes, for records whose text is their note's.
 * @param onOpen - Open a record (its note or its page).
 */
export function CallsTab({ encounters, notes, onOpen, looseNotes = [], onOpenNote }: {
  encounters: Encounter[];
  notes: Note[];
  onOpen: (record: Encounter) => void;
  /** Doctors' notes with no visit (notesWithoutVisit): listed as chart notes, opening the note. */
  looseNotes?: Note[];
  onOpenNote?: (note: Note) => void;
}) {
  const [filter, setFilter] = useState<CallFilter>("all");
  // The year picked, as on Visits; null: every year.
  const [pickedYear, setPickedYear] = useState<string | null>(null);
  const [view, setView] = useState<VisitsView>(() => readView(VIEW_KEY));
  const pickView = (next: VisitsView) => {
    setView(next);
    saveView(next, VIEW_KEY);
  };
  const looseById = new Map(looseNotes.map((note) => [`note:${note.id}`, note]));
  const records = [...nonVisits(encounters), ...looseNotes.map(asChartNote)];
  const ofFilter = filter === "all" ? records : records.filter((r) => callFilterOf(r) === filter);
  const years = [...new Set([...ofFilter.map((r) => r.start.slice(0, 4)).filter((y) => /^\d{4}$/.test(y)), ...(pickedYear ? [pickedYear] : [])])]
    .sort((a, b) => b.localeCompare(a));
  const all = pickedYear ? ofFilter.filter((r) => r.start.startsWith(pickedYear)) : ofFilter;
  const paged = usePaged(all, `${filter}|${pickedYear ?? ""}`);
  const shown = paged.shown;
  const count = (id: CallFilter) => (id === "all" ? records.length : records.filter((r) => callFilterOf(r) === id).length);

  return (
    <div className="grid gap-4">
      <p className={cn("!mb-0 px-1", subTextClass)}>Phone calls, portal messages, letters and chart notes. These aren’t in-person visits.</p>

      {/* The same underline tabs as the Visits filter. */}
      {records.length > 0 && (
        <UnderlineTabs<CallFilter>
          label="Filter calls, messages and letters"
          active={filter}
          onChange={setFilter}
          tabs={CALL_FILTERS.filter((f) => f.id === "all" || count(f.id) > 0 || f.id === filter).map((f) => ({ id: f.id, label: f.label, count: count(f.id) }))}
        />
      )}

      {records.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {years.length > 1 && (
            <div className="w-full sm:w-[280px]">
              {/* One year at a time, as on Visits. */}
              <Select
                id="calls-years"
                aria-label="Years"
                value={pickedYear ?? ""}
                onChange={(value) => setPickedYear((value as string) || null)}
                options={[{ value: "", label: "All years" }, ...years.map((year) => ({ value: year, label: year }))]}
              />
            </div>
          )}
          <div role="group" aria-label="View" className="ml-auto flex gap-2">
            {(["list", "cards"] as const).map((v) => (
              <Button
                key={v}
                variant="outline"
                aria-pressed={view === v}
                onClick={() => pickView(v)}
                className={cn(filterButtonClass, "!h-8", view === v ? activeFilterClass : inactiveFilterClass)}
              >
                <Icon icon={v === "list" ? "lucide:list" : "lucide:layout-grid"} className="h-3.5 w-3.5" />
                {v === "list" ? "List" : "Cards"}
              </Button>
            ))}
          </div>
        </div>
      )}

      {shown.length > 0 ? (
        groupCalls(shown).map(({ heading, records: inGroup }, index) => (
          <section key={`${heading}-${index}`} aria-label={heading || "Undated"} className="grid gap-2">
            {heading && <h2 className="!mb-0 px-1 text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">{heading}</h2>}
            <div className={view === "cards" ? "grid gap-4 md:grid-cols-2 2xl:grid-cols-3" : "grid gap-2"}>
              {inGroup.map((record) => {
                const Item = view === "cards" ? CallCard : CallRow;
                const loose = looseById.get(record.id);
                return loose
                  ? <Item key={record.id} record={record} notes={[loose]} onOpen={() => onOpenNote?.(loose)} />
                  : <Item key={record.id} record={record} notes={notesForVisit(record, notes)} onOpen={() => onOpen(record)} />;
              })}
            </div>
          </section>
        ))
      ) : (
        <EmptyState
          icon={<Icon icon="lucide:phone" className={mutedIconClass} />}
          title={pickedYear ? `No calls, messages or letters in ${pickedYear}` : "No calls, messages or letters"}
          description={pickedYear ? "Choose All years to see every one" : "Phone calls, portal messages and letters from your care team will appear here"}
        />
      )}
      <LoadOlderButton show={paged.hasMore} onClick={paged.more} />
    </div>
  );
}

/** A note with no visit, in a record's shape: a chart note on its date, by its author. */
function asChartNote(note: Note): Encounter {
  return {
    id: `note:${note.id}`, sourceIds: [], class: "other", group: "non_visit", kind: "chart_note",
    typeCode: "", typeRaw: noteTypeLabel(note), typeLabel: noteTypeLabel(note),
    start: note.date ?? "", hasTime: false, practitioner: note.author || undefined,
    sources: note.sources, noteIds: [note.id],
  };
}

interface ItemProps {
  record: Encounter;
  notes: Note[];
  onOpen: () => void;
}

function facts(record: Encounter, notes: Note[]) {
  const type = recordTypeLabel(record);
  const date = formatDateOnly(record.start);
  return {
    title: [type, date].filter(Boolean).join(" · "),
    who: [record.practitioner, record.department || record.specialty || record.facility].filter(Boolean).join(" · "),
    text: recordText(record, notes).trim(),
  };
}

// function(Preview)
/**
 * The first `lines` lines of a record's text in a box of exactly that height
 * (every row, every card the same size), and a "Read more" line, shown only
 * when the text doesn't fit and kept as blank space when it does. It is part
 * of the row's button: the whole row opens the record.
 */
function Preview({ text, lines }: { text: string; lines: 2 | 4 }) {
  const box = useRef<HTMLSpanElement>(null);
  // Before layout (or with none, as in tests): long or many-line text is taken as too long.
  const [more, setMore] = useState(() => text.split("\n").length > lines || text.length > lines * 80);
  useLayoutEffect(() => {
    const el = box.current;
    if (el && el.clientHeight > 0) setMore(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines]);
  return (
    <>
      <span
        ref={box}
        className={cn(
          "block overflow-hidden whitespace-pre-line text-sm leading-5",
          lines === 2 ? "line-clamp-2 h-10" : "line-clamp-4 h-20",
          text ? "text-[#111827]" : "text-[#9CA3AF]",
        )}
      >
        {text || "No text was recorded for this one."}
      </span>
      <span aria-hidden={!more} className={cn("flex h-5 items-center gap-1 text-sm font-bold text-[#E4002B]", !more && "invisible")}>
        Read more <Icon icon="lucide:arrow-right" className="h-3.5 w-3.5" />
      </span>
    </>
  );
}

// function(CallRow)
/** One record as a list row; the whole row opens it. */
function CallRow({ record, notes, onOpen }: ItemProps) {
  const { title, who, text } = facts(record, notes);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={title}
      className={cn("flex w-full items-start gap-3 border p-3 text-left sm:p-4", clickableCardClass, "!rounded-2xl")}
    >
      <KindTile kind={visitKind(record)} size="sm" />
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-sm", titleClass)}>{title}</span>
        <span className="block h-4 truncate text-xs text-[#6B7280]">{who}</span>
        <span className="mt-1.5 block"><Preview text={text} lines={2} /></span>
      </span>
      <Icon icon="lucide:chevron-right" className="mt-1 h-5 w-5 shrink-0 text-[#9CA3AF]" aria-hidden="true" />
    </button>
  );
}

// function(CallCard)
/** One record as a card; the whole card opens it. */
function CallCard({ record, notes, onOpen }: ItemProps) {
  const { title, who, text } = facts(record, notes);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={title}
      className={cn("flex h-full w-full flex-col gap-3 border p-5 text-left", clickableCardClass)}
    >
      <span className="flex w-full items-start gap-3">
        <KindTile kind={visitKind(record)} />
        <span className="min-w-0 flex-1">
          <span className={cn("block truncate", titleClass)}>{title}</span>
          <span className="block h-5 truncate text-sm text-[#6B7280]">{who}</span>
        </span>
      </span>
      <span className="block w-full"><Preview text={text} lines={4} /></span>
    </button>
  );
}
